/**
 * Mengeluarkan kurva sebuah sumur sebagai berkas LAS atau CSV.
 *
 * Yang dipilih di sini bukan "ekspor" melainkan tiga hal yang menentukan
 * apakah berkasnya berguna di ujung sana: kurva mana, selang kedalaman mana,
 * dan bentuk apa. Ketiganya ditanyakan sekaligus, sebab ketiganya diputuskan
 * sekaligus oleh orang yang tahu berkas itu akan dibuka di mana.
 *
 * Kepala berkasnya disusun server — sumur, lapangan, cekungan, CRS, set kurva
 * berikut versinya, siapa yang mengeluarkan dan kapan — dan itu memang
 * tempatnya: yang tahu dari mana angkanya datang adalah yang menyimpannya,
 * bukan layar yang kebetulan sedang membukanya.
 */
import { useMemo, useState } from "react";

import { api } from "../api";
import { Button, Checkbox, Modal, Notice, Segmented, Toggle } from "./ui";

export interface ExportCurve {
  id: number;
  mnemonic: string;
  unit: string;
  /** Nama set beserta versinya, dicetak di daftar supaya dua GR bisa dibedakan. */
  setLabel: string;
  /** Milik set yang sedang aktif. Daftar ini dibuka pada yang aktif saja. */
  active: boolean;
}

export function ExportDialog({
  wellName,
  wellId,
  curves,
  initial,
  depthRange,
  onClose,
  onDone,
}: {
  wellName: string;
  wellId: number;
  /** Kurva yang boleh dipilih — seluruh kurva sumur ini. */
  curves: ExportCurve[];
  /**
   * Yang tercentang ketika dialog terbuka: baris pohon yang membukanya.
   *
   * Daftarnya tetap memuat seluruh kurva sumur, sebab "ekspor GR" hampir
   * selalu berarti "GR dan beberapa lainnya" — dan mencari kurva kedua lewat
   * menutup dialog, menekan baris lain, lalu mencentang ulang adalah jalan
   * memutar yang tidak menghasilkan apa-apa.
   */
  initial: number[];
  /** Selang penuh sumur ini, dipakai sebagai bawaan kotak kedalaman. */
  depthRange: [number, number] | null;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const [kind, setKind] = useState<"las" | "csv">("las");
  const [picked, setPicked] = useState<Set<number>>(() => new Set(initial));
  const [top, setTop] = useState("");
  const [base, setBase] = useState("");
  /**
   * Versi lama disembunyikan sampai diminta.
   *
   * Tiap perhitungan menerbitkan satu versi set kurva, jadi sebuah sumur yang
   * sudah lama dikerjakan membawa empat puluh versi berisi delapan kurva
   * masing-masing. Mendaftar ketiga ratusnya sekaligus berarti mencari PHIE
   * yang dimaksud di antara empat puluh PHIE yang namanya sama — dan yang
   * paling sering diekspor adalah yang aktif, yaitu yang sedang tergambar di
   * lembar log di sebelah.
   */
  const [allVersions, setAllVersions] = useState(false);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  // Kurva dari set yang berbeda boleh dikeluarkan bersama, dan itu memang
  // kadang yang diminta — GR mentah bersama PHIE hasil hitung. Yang tidak
  // boleh adalah diam ketika sumbunya berbeda; itu dikatakan di sini, dan
  // dicatat lagi di kepala berkasnya.
  const mixed = useMemo(() => {
    const sets = new Set(curves.filter((c) => picked.has(c.id)).map((c) => c.setLabel));
    return sets.size > 1;
  }, [curves, picked]);

  const shown = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return curves.filter(
      (curve) =>
        (allVersions || curve.active || picked.has(curve.id)) &&
        (!needle || curve.mnemonic.toLowerCase().includes(needle) ||
          curve.setLabel.toLowerCase().includes(needle)),
    );
  }, [curves, allVersions, picked, search]);

  const toggle = (id: number) =>
    setPicked((current) => {
      const next = new Set(current);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const keluarkan = async () => {
    setBusy(true);
    setProblem(null);
    try {
      const text = await api.get<string>(`/api/exports/logs/${wellId}/`, {
        curves: [...picked].join(","),
        kind,
        top: top.trim(),
        base: base.trim(),
      });
      // Diunduh dari peramban, bukan dibuka di tab baru: sebuah berkas LAS
      // yang tampil sebagai teks di layar menuntut penggunanya menyalin
      // seluruhnya ke editor sebelum bisa dipakai.
      const blob = new Blob([text], {
        type: kind === "csv" ? "text/csv;charset=utf-8" : "text/plain;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${wellName.replace(/[^A-Za-z0-9-_]/g, "-")}.${kind}`;
      anchor.click();
      URL.revokeObjectURL(url);
      onDone(`${picked.size} curves of ${wellName} exported as ${kind.toUpperCase()}.`);
      onClose();
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={`Export curves — ${wellName}`}
      width={470}
      onClose={onClose}
      footer={
        <>
          <span style={{ flex: 1, font: "400 10.5px var(--font-sans)", color: "var(--ink-5)" }}>
            {picked.size} kurva dipilih
          </span>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" busy={busy} disabled={picked.size === 0} onClick={keluarkan}>
            Ekspor {kind.toUpperCase()}
          </Button>
        </>
      }
    >
      {problem && <Notice tone="error">{problem}</Notice>}

      <Segmented
        value={kind}
        options={[
          { value: "las" as const, label: "LAS 2.0" },
          { value: "csv" as const, label: "CSV" },
        ]}
        onChange={setKind}
      />
      <span style={{ font: "400 10px/1.5 var(--font-sans)", color: "var(--ink-5)" }}>
        {kind === "las"
          ? "A LAS 2.0 file with the ~Well, ~Curve and ~Parameter blocks filled in: well, field, CRS, curve set and its version, and who exported it. Samples that were never measured are written −999.25."
          : "A CSV with one header row carrying the units. A few comment lines prefixed with # come first — skipped by any CSV reader, and readable by a person."}
      </span>

      <div style={{ display: "flex", gap: 9 }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
          <span style={{ font: "400 10px var(--font-sans)", color: "var(--ink-6)" }}>
            Dari (m MD)
          </span>
          <input
            value={top}
            placeholder={depthRange ? depthRange[0].toFixed(1) : "everything"}
            onChange={(event) => setTop(event.target.value)}
            style={boxStyle}
          />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
          <span style={{ font: "400 10px var(--font-sans)", color: "var(--ink-6)" }}>
            Sampai (m MD)
          </span>
          <input
            value={base}
            placeholder={depthRange ? depthRange[1].toFixed(1) : "everything"}
            onChange={(event) => setBase(event.target.value)}
            style={boxStyle}
          />
        </label>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span className="label">Curves</span>
        <span style={{ flex: 1 }} />
        <Button onClick={() => setPicked(new Set(shown.map((c) => c.id)))}>All</Button>
        <Button onClick={() => setPicked(new Set())}>Clear</Button>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <input
          value={search}
          placeholder="Search mnemonic or set…"
          onChange={(event) => setSearch(event.target.value)}
          style={{ ...boxStyle, flex: 1, font: "400 11px var(--font-sans)" }}
        />
        <Toggle
          checked={allVersions}
          onChange={setAllVersions}
          label={`Older versions (${curves.length - curves.filter((c) => c.active).length})`}
        />
      </div>

      <div className="scroll-y" style={{ maxHeight: 240, display: "flex",
                                         flexDirection: "column", gap: 3 }}>
        {shown.length === 0 && (
          <span style={{ font: "400 10.5px var(--font-sans)", color: "var(--ink-6)" }}>
            No curve matches.
          </span>
        )}
        {shown.map((curve) => (
          <Checkbox
            key={curve.id}
            checked={picked.has(curve.id)}
            onChange={() => toggle(curve.id)}
            label={curve.mnemonic}
            meta={`${curve.unit || "—"} · ${curve.setLabel}`}
          />
        ))}
      </div>

      {mixed && (
        <Notice>
          Curves from more than one set are picked. They can leave together, and the ones
          whose depth axis differs are resampled to the smallest step — the file header
          names which curves that happened to.
        </Notice>
      )}
    </Modal>
  );
}

const boxStyle = {
  height: 26,
  padding: "0 8px",
  background: "var(--control)",
  border: "1px solid var(--border-control)",
  borderRadius: "var(--radius)",
  font: "400 11px var(--font-mono)",
  color: "var(--ink)",
  outline: "none",
} as const;
